import { useMemo } from 'react';
import { FiservCardHolder, FiservCardStatusEnum } from '@common-fe/common-fe';

import { useGetCardsQuery } from '@/modules/transaction/queries/useGetCards.query';
import { DependentStatusEnum } from '@/modules/user/components/ManageProfile/components/Dependents/dependents.types';
import useDependentsQuery from '@/modules/user/components/ManageProfile/components/Dependents/queries/useDependents.query';

interface Props {
  employeeId?: string;
}

const EXCLUDED_CARD_STATUSES = [
  FiservCardStatusEnum.CLOSED,
  FiservCardStatusEnum.PENDING_CLOSED,
  FiservCardStatusEnum.LOST,
  FiservCardStatusEnum.STOLEN,
  FiservCardStatusEnum.NOT_SENT,
  FiservCardStatusEnum.PENDING_REPLACE,
];

export const useFilteredDependents = ({ employeeId }: Props) => {
  const { formattedData: dependents, isLoading: dependentsLoading } = useDependentsQuery([DependentStatusEnum.ACTIVE]);
  const { cardMap, isLoading: cardHoldersLoading } = useGetCardsQuery(employeeId);

  const filteredDependents = useMemo(() => {
    if (!dependents.length) return [];
    if (!cardMap || Object.keys(cardMap).length === 0) {
      return dependents.map(dependent => ({
        dependentId: dependent.id!,
        firstName: dependent.firstName || '',
        lastName: dependent.lastName || '',
        name: `${dependent.firstName || ''} ${dependent.lastName || ''}`,
        dateOfBirth: dependent.birthOn || '',
        relationship: dependent.relationshipType || '',
        employeeId: employeeId || '',
      })) as FiservCardHolder[];
    }

    const dependentsWithActiveCards = new Set<string>();

    Object.values(cardMap).forEach(holderCards => {
      Object.entries(holderCards).forEach(([status, cards]) => {
        if (!EXCLUDED_CARD_STATUSES.includes(status as FiservCardStatusEnum) && cards.length > 0) {
          cards.forEach(card => {
            if (card.holder.dependentId) {
              dependentsWithActiveCards.add(card.holder.dependentId);
            }
          });
        }
      });
    });

    return dependents
      .filter(dependent => dependent.id && !dependentsWithActiveCards.has(dependent.id))
      .map(dependent => ({
        dependentId: dependent.id!,
        firstName: dependent.firstName || '',
        lastName: dependent.lastName || '',
        name: `${dependent.firstName || ''} ${dependent.lastName || ''}`,
        dateOfBirth: dependent.birthOn || '',
        relationship: dependent.relationshipType || '',
        employeeId: employeeId || '',
      })) as FiservCardHolder[];
  }, [dependents, cardMap, employeeId]);

  return {
    filteredDependents: filteredDependents as FiservCardHolder[],
    isLoading: dependentsLoading || cardHoldersLoading,
  };
};